'use client';

import React from 'react';
import styled from 'styled-components';

export interface KVRowProps {
  /** Label rendered in the left column. Eyebrow style — uppercase, mono-spaced. */
  label: React.ReactNode;
  /**
   * Value rendered in the right column. May be a string, `MonoValue`,
   * `MetaChip`, `Plate`, or any composition.
   *
   * If `value` is `null` or `undefined` and `renderEmpty` is false (default),
   * the row renders nothing. This makes optional record fields easy to omit
   * in JSX without conditional wrapping.
   */
  value: React.ReactNode;
  /** Render the row even when `value` is nullish. Defaults to `false`. */
  renderEmpty?: boolean;
  className?: string;
}

/**
 * One row of a definition list — label on the left (eyebrow style),
 * value on the right. Use inside {@link KVList}.
 *
 * Renders nothing when the value is nullish unless `renderEmpty` is set,
 * so optional record fields don't need conditional wrapping in JSX.
 */
export function KVRow({ label, value, renderEmpty = false, className }: KVRowProps) {
  if (!renderEmpty && (value === null || value === undefined)) return null;
  return (
    <Row className={className}>
      <Key>{label}</Key>
      <Value>{value}</Value>
    </Row>
  );
}

export interface KVListProps extends React.HTMLAttributes<HTMLElement> {
  /** Width of the label column. Defaults to `130px`. */
  labelWidth?: string;
  children?: React.ReactNode;
}

/**
 * Definition list for record-detail surfaces — drawer bodies, detail
 * panels, inspector cards. Pair with {@link KVRow}.
 *
 * The label column is fixed-width (130 px by default) so values align
 * vertically across rows, matching the dispatcher's "scan one column"
 * reading pattern.
 */
export function KVList({ labelWidth, children, ...rest }: KVListProps) {
  return (
    <List $labelWidth={labelWidth ?? '130px'} {...rest}>
      {children}
    </List>
  );
}

const List = styled.dl<{ $labelWidth: string }>`
  display: grid;
  grid-template-columns: ${(p) => p.$labelWidth} 1fr;
  row-gap: ${(p) => p.theme.spaceAlias.sm};
  column-gap: ${(p) => p.theme.spaceAlias.sm};
  margin: 0;
`;

const Row = styled.div`
  display: contents;
`;

const Key = styled.dt`
  margin: 0;
  align-self: center;
  font-size: ${(p) => p.theme.fontSize.xs};
  font-weight: ${(p) => p.theme.fontWeight.semibold};
  font-family: ${(p) => p.theme.fontFamily.ui};
  color: ${(p) => p.theme.colors.neutral.inkSoft};
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;

const Value = styled.dd`
  margin: 0;
  align-self: center;
  font-size: ${(p) => p.theme.fontSize.sm};
  color: ${(p) => p.theme.colors.neutral.ink};
  min-width: 0;
`;
